import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";

import agentClient from "@apis/agentClient";
import type { EnrolledAgent } from "@apis/agentClient";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { hasFeature } from "@hooks/useFeatures";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@hooks/useLanguage";
import { UserPermission } from "@/types/generated/UserPermission";
import { hasPermission } from "@hooks/usePermissions";
import type { RootState } from "@/types/RootState";
import type { MenuRoute } from "./menuTypes";

/**
 * The state the Agents group is registered under, in every menu that carries
 * it. The hook finds the group by this value rather than by position.
 */
export const AGENT_GROUP_STATE = "openAgents";

/**
 * Menu route with the fields the agent entries add on top of MenuRoute.
 */
export interface AgentMenuRoute extends MenuRoute {
  category?: string;
  type?: string;
}

export interface AgentMenuItemsOptions {
  /**
   * Whether this menu is the one that carries the group — the Home menu for a
   * login that has one, the own level for a login that does not.
   *
   * When false there is no fetch and the group is hidden, but `onApplied` still
   * fires, so a caller gating its `loading` on it is not left waiting for a
   * request that will never be made.
   */
  enabled: boolean;
  /** The organisation whose enrolled agents to list — the login's own. */
  orgId?: number;
  /**
   * Runs once the views have been applied (or skipped). Used by the menus to
   * flip their first-init gate and notify their consumers.
   */
  onApplied?: () => void;
}

/**
 * Fetches the org's enrolled agents and injects the favourited ones into the
 * group registered under {@link AGENT_GROUP_STATE}, followed by "All agents".
 *
 * Shared by the Home menu and the own-level menu (STIC-16186 R2), the same way
 * {@link useDashboardMenuItems} is: a login with a Home menu gets the group
 * there, one without it — SYSTEM always, a partner without
 * `legacy_partner_home_menu` — gets it on the own level. Before this, `/all_agents`
 * was registered by the Home menu alone, so those logins could not reach it at all.
 *
 * Unlike dashboards, the filtering is done here rather than by the server. The
 * endpoint that filters (`getMenuAgentsForOrg`) returns only the favourites, and
 * the group's visibility needs the *total* count as well (R8 below), so one
 * `getAgentsForOrg` call answers both.
 *
 * @param setRoutes - state setter for the menu's routes array
 * @param options - see {@link AgentMenuItemsOptions}
 */
const useAgentMenuItems = <T extends MenuRoute>(
  setRoutes: React.Dispatch<React.SetStateAction<T[]>>,
  { enabled, orgId, onApplied }: AgentMenuItemsOptions
): void => {
  const global = useSelector((state: RootState) => state.all);

  /**
   * Hide the group and leave it with no entries at all.
   *
   * Clearing `views` is the point, not a tidy-up: `useAvailibleMenu` merges a
   * section's children into `allPaths` **unconditionally**, hidden section or
   * not (only `availableMenuPaths` respects the section's `view`). Entries left
   * behind here would put /all_agents and /agents/<name> in the own-level menu's
   * `allPaths` for a login whose group lives in the Home menu — and a bare path
   * resolves to the own level first (rule 1), so the resolver would hand those
   * pages to a menu that does not offer them and the guard would answer
   * /no_access.
   */
  const hideAgentGroup = useCallback(() => {
    setRoutes((oldRoutes) => {
      const route = oldRoutes.find((x) => x.state === AGENT_GROUP_STATE);
      if (route) {
        route.view = false;
        route.views = [];
      }
      return [...oldRoutes];
    });
    onApplied?.();
  }, [setRoutes, onApplied]);

  const applyAgentViews = useCallback(
    (allAgents: EnrolledAgent[]) => {
      const entryView =
        hasFeature(FeatureKey.agents) && hasPermission(UserPermission.MENU_HOME_AGENT);
      // R8: show the group only when the org has at least one enrolled agent —
      // on top of the feature key, the permission, and this menu being the one
      // that carries the group at all. A group that ends up hidden is cleared
      // rather than filled, for the `allPaths` reason above.
      if (!(enabled && entryView && allAgents.length > 0)) {
        hideAgentGroup();
        return;
      }

      const agentViews: AgentMenuRoute[] = [];

      // Only favourited agents are listed (favouriteForAll OR user-favourited),
      // mirroring the server-side getMenuAgentsForOrg filter.
      const menuAgents = allAgents.filter((agent) => agent.favouriteForAll || agent.favorite);

      for (let i = 0; i < menuAgents.length; i++) {
        const agent = menuAgents[i];
        agentViews.push({
          view: entryView,
          to: "/agents/" + encodeURIComponent(agent.name),
          layout: "",
          type: "agent",
          name: agent.title || agent.name,
          category: "agent",
        });
      }

      // "All agents" is always last, under the favourited ones.
      agentViews.push({
        view: entryView,
        to: "/all_agents",
        layout: "",
        type: "agent",
        name: translate(LanguageKey.all_agents_key),
        category: "agent",
      });

      setRoutes((oldRoutes) => {
        const route = oldRoutes.find((x) => x.state === AGENT_GROUP_STATE);
        if (route) {
          route.view = true;
          route.views = [...agentViews];
        }
        return [...oldRoutes];
      });
      onApplied?.();
    },
    [setRoutes, enabled, hideAgentGroup, onApplied]
  );

  useEffect(() => {
    // Not this menu's group: hide it and report done without fetching.
    if (!enabled) {
      hideAgentGroup();
      return;
    }

    if (hasFeature(FeatureKey.agents) && hasPermission(UserPermission.AGENT_VIEW) && orgId) {
      agentClient
        .getAgentsForOrg(orgId)
        .then((response) => {
          applyAgentViews((response.data || []) as EnrolledAgent[]);
        })
        .catch(() => {
          applyAgentViews([]);
        });
    } else {
      applyAgentViews([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, orgId, global.reloadSidebar]);
};

export default useAgentMenuItems;
